import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import IHistoriaClinica from "../../../types/HistoriaClinica";
import { createTheme } from '@mui/material/styles';
import PacienteService from "../../../services/PacienteService";
import { Typography, TextField, Button, Avatar, Paper, Container, Grid, CircularProgress, Alert, Snackbar, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import IPaciente from "../../../types/Paciente";
import Utilities from "../../../common/Utilities";
import IAlert from "../../../types/Alert";
import * as constantes from "../../../common/Constantes";
import ConfigUtils from "../../../utils/Config";

const MAX_RED_FLAGS = 6;
const MIN_LENGTH = 3;
const MAX_LENGTH = 180;

const HistoriaClinicaForm = () => {
    const theme = createTheme();
    const history = useNavigate();

    let location = useLocation();
    const [paciente] = useState<IPaciente>(location.state.paciente);
    const [historiaClinica] = useState<IHistoriaClinica>(location.state.historiaClinica);

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const {register, watch, handleSubmit, reset, setValue} = useForm<IHistoriaClinica>({ defaultValues: { ...historiaClinica, redFlags: historiaClinica?.redFlags ?? [] } });

    const userRole: number = ConfigUtils.getUserRole();

    const onSubmit = (data: IHistoriaClinica) => {
        setLoading(true);

        const normalizedRedFlags = (data.redFlags ?? [])
            .map(flag => flag.trim())
            .filter(Boolean);

        // VALIDACIÓN: máximo número
        if (normalizedRedFlags.length > MAX_RED_FLAGS) {
          setLoading(false);
          // aquí puedes usar toast / snackbar
          setAlert({ open: true, severity: 'warning', msg: `Máximo ${MAX_RED_FLAGS} red flags permitidas`});
          return;
        }
    
        // VALIDACIÓN: longitud individual
        const invalidLength = normalizedRedFlags.find(
          flag => flag.length < MIN_LENGTH || flag.length > MAX_LENGTH
        );
    
        if (invalidLength) {
          setLoading(false);
          setAlert({ open: true, severity: 'warning', msg: `Cada red flag debe tener entre ${MIN_LENGTH} y ${MAX_LENGTH} caracteres`});
          return;
        }
    
        const normalizedData: IHistoriaClinica = {
          ...data,
          redFlags: normalizedRedFlags
        };

        historiaClinica ? updateHistoriaClinica(normalizedData) : saveHistoriaClinica(normalizedData);
    };

    const clear = () => {
        reset();
        history(-1);
    };

    const saveHistoriaClinica = (data: IHistoriaClinica) => {
        const isEmpty = Object.values(data).every(value => {
          if (Array.isArray(value)) {
            return value.length === 0;
          }
      
          return Utilities.isNullEmptyUndefined(value);
        });

        if (isEmpty) {
          setAlert({
            open: true,
            severity: 'warning',
            msg: 'No hay datos que guardar'
          });
          setLoading(false);
          return;
        }

        data.pacienteId = paciente._id;
        PacienteService.createHClinica(data)
        .then((_: any) => {
            setLoading(false);
            history(`${constantes.R_PACIENTE}?${constantes.QP_ID}=${paciente._id}`, { state: { open: true, severity: 'success', msg: 'Historia clínica guardada correctamente' } });
        })
        .catch ((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'HistoriaClinicaForm.Component.saveHistoriaClinica', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                setLoading(false);
            }
        });
    };

    const updateHistoriaClinica = (data: IHistoriaClinica) => {
        PacienteService.updateHClinica(paciente._id, data)
        .then((_: any) => {
            setLoading(false);
            history(`${constantes.R_PACIENTE}?${constantes.QP_ID}=${paciente._id}`, { state: { open: true, severity: 'success', msg: 'Historia clínica actualizada correctamente' } });
        })
        .catch ((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'HistoriaClinicaForm.Component.updateHistoriaClinica', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                setLoading(false);
            }
        });
    };

    const handleChange = (e: any) => {
        setValue(e.target.name, e.target.value);
    };

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    useEffect(() => {
        if (historiaClinica) {
            reset({
                ...historiaClinica,
                redFlags: historiaClinica.redFlags ?? []
            });
        }
    }, [historiaClinica, reset]);

    const redFlags = watch("redFlags") ?? [];
    const hasEmptyFlag = (redFlags ?? []).some(f => !f?.trim());
    const isMaxFlags = (redFlags?.length ?? 0) >= 6;

    const isDisabled = isMaxFlags || hasEmptyFlag;

    const tooltipMessage = isMaxFlags
      ? "Has alcanzado el máximo de 6 red flags"
      : hasEmptyFlag
      ? "Completa o elimina las red flags vacías antes de añadir otra"
      : "Añadir red flag";

    try {
        return (
            userRole != constantes.ROLES.SECRETARIO ?
            <>
                <Container maxWidth="lg">
                <Paper elevation={3} sx={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    
                    <Typography variant="h4" sx={{ display: 'inline-flex', margin: theme.spacing(1, 0, 0) }}><Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><EditIcon /></Avatar><div style={{ margin: '10px auto' }}>{historiaClinica ? 'Editar historia clínica' : 'Añadir historia clínica'}</div></Typography>
                    <Typography variant="h6" >{`Paciente: ${paciente.nombre} ${paciente.apellidos}`}</Typography>
                        <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ width: '90%' }}>
                            <Typography style={{ fontWeight: 'bold' }} variant="subtitle1">Antecedentes personales:</Typography>

                            <Grid container spacing={1}>
                                <Grid item md={6}>
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.medicacion} {...register("medicacion")} onChange={handleChange} variant="outlined" label="Medicación" margin="normal" fullWidth autoFocus />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.traumaticas} {...register("traumaticas")} onChange={handleChange} variant="outlined" label="Traumáticas" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.intervenciones} {...register("intervenciones")} onChange={handleChange} variant="outlined" label="Intervenciones quirúrgicas, cicatrices (diagnóstico y evolución)" margin="normal" fullWidth />

                                </Grid>
                                <Grid item md={6}>
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.alergias} {...register("alergias")} onChange={handleChange} variant="outlined" label="Alergias" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.enfermedades} {...register("enfermedades")} onChange={handleChange} variant="outlined" label="Enfermedades (año, diagnóstico, tratamiento y evolución)" margin="normal" fullWidth />

                                </Grid>
                            </Grid>
                            <Typography style={{ fontWeight: 'bold' }} variant="subtitle1">Antecedentes familiares:</Typography>
                            <TextField multiline rows={3} defaultValue={historiaClinica?.antecedentesFamilliares} {...register("antecedentesFamilliares")} onChange={handleChange} variant="outlined" margin="normal" fullWidth />
                            
                            <Typography style={{ fontWeight: 'bold' }} variant="subtitle1">Anamnesis por aparatos:</Typography>

                            <Grid container spacing={1}>
                                <Grid item md={6}>
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.sistemaNervioso} {...register("sistemaNervioso")} onChange={handleChange} variant="outlined" label="Sistema nervioso (sueño/SNC/SNP)" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.aparatoCirculatorio} {...register("aparatoCirculatorio")} onChange={handleChange} variant="outlined" label="Aparato circulatorio" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.aparatoRespiratorio} {...register("aparatoRespiratorio")} onChange={handleChange} variant="outlined" label="Aparato respiratorio" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.aparatoGenitoUrinario} {...register("aparatoGenitoUrinario")} onChange={handleChange} variant="outlined" label="Aparato genito-urinario" margin="normal" fullWidth />
                                </Grid>
                                <Grid item md={6}>
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.aparatoLocomotor} {...register("aparatoLocomotor")} onChange={handleChange} variant="outlined" label="Aparato locomotor" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.aparatoDigestivo} {...register("aparatoDigestivo")} onChange={handleChange} variant="outlined" label="Aparato digestivo" margin="normal" fullWidth />
                                    <TextField multiline rows={2} defaultValue={historiaClinica?.sistemaEndocrino} {...register("sistemaEndocrino")} onChange={handleChange} variant="outlined" label="Sistema endocrino" margin="normal" fullWidth />
                                </Grid>

                                <Grid item md={12}>
                                    <Typography style={{ fontWeight: 'bold' }} variant="subtitle1">
                                      Red Flags:
                                    </Typography>
                                </Grid>

                                <Grid item md={12}>
                                    {redFlags.map((flag, index) => (
                                      <Grid container spacing={1} key={index} alignItems="center">
                                        <Grid item xs={10}>
                                          <TextField
                                            multiline
                                            fullWidth
                                            value={flag}
                                            error={flag.length > 0 && (flag.length < 3 || flag.length > 180)}
                                            helperText={
                                              flag.length > 0 && flag.length < 3
                                                ? "Mínimo 3 caracteres"
                                                : flag.length > 180
                                                ? "Máximo 180 caracteres"
                                                : `${flag.length}/180`
                                            }
                                            onChange={(e) => {
                                              const updated = [...redFlags];
                                              updated[index] = e.target.value;
                                              setValue("redFlags", updated, {
                                                shouldDirty: true,
                                                shouldValidate: true
                                              });
                                            }}
                                            sx={{ marginTop: theme.spacing(1) }}
                                          />
                                        </Grid>
                                    
                                        <Grid item xs={2}>
                                          <Button
                                            color="error"
                                            fullWidth
                                            onClick={() => {
                                              const updated = redFlags.filter((_, i) => i !== index);
                                              setValue("redFlags", updated, {
                                                shouldDirty: true
                                              });
                                            }}
                                          >
                                            Borrar
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    ))}
                                    <Tooltip title={tooltipMessage} >
                                        <span>
                                            <Button
                                              variant="outlined"
                                              onClick={() => { setValue("redFlags", [...redFlags, ""]) }}
                                              disabled={isDisabled}
                                              sx={{ marginTop: theme.spacing(1) }}
                                            >
                                              + Añadir red flag
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>

                                <Grid style={{ margin: '10px auto' }} justifyContent="center" alignItems="center">
                                { loading ? <CircularProgress /> : 
                                <>
                                    <Button sx={{ margin: theme.spacing(2, 3, 1, 0) }} variant="contained" color="primary" type="submit" >Guardar</Button>
                                    <Button sx={{ margin: theme.spacing(2, 3, 1, 0) }} variant="contained" color="secondary" onClick={clear}>Cancelar</Button>
                                </>
                                }
                                </Grid>
                            </Grid>
                        </form>
                    
                </Paper>
                </Container>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </>
            :
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open>
                <Alert severity="warning" sx={{ width: '100%' }} >Usuario no autorizado</Alert>
            </Snackbar>
        );
    } catch (e: any) {
        const error: ILog = { method: 'HistoriaClinicaForm.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
    
};

export default HistoriaClinicaForm;